import { useContext } from "react";
import { AppContext } from "@edx/frontend-platform/react";
import { getCookies } from "@edx/frontend-platform/i18n/lib";

/**
 * Determines the user's role and permissions based on authenticated user context and organization
 * @returns {Object} Role and permission information
 * @property {string} role - The user's role in the current organization (e.g., 'super_admin', 'staff', 'learner')
 * @property {string[]} permissions - Array of permission strings assigned to the user
 * @property {boolean} hasCreateProgram - Whether user can create programs
 * @property {boolean} hasViewAnalytics - Whether user can view analytics
 * @property {boolean} hasLibraryView - Whether user can view library
 * @property {boolean} hasLibraryCreate - Whether user can create library items
 * @property {boolean} hasScheduleCreate - Whether user can create schedules
 * @property {boolean} hasCertificateView - Whether user can view certificates
 * @property {boolean} hasNotificationSend - Whether user can send notifications
 * @property {boolean} hasBillingView - Whether user can view billing information
 * @property {boolean} hasUserManagement - Whether user can manage users
 * @property {boolean} hasPlatformSettings - Whether user can modify platform settings
 * @property {boolean} hasAiStudio - Whether user has access to AI Studio
 */
export function getRoleAndPermissions() {
  const { authenticatedUser } = useContext(AppContext);
  const { roles } = authenticatedUser;

  const orgName = getCookies().get('orgName');

  // Find org with staff/admin role
  const isStaffOrAdmin = role => {
    const roleType = role.split('-').at(-1);
    return roleType.startsWith('staff') || roleType.startsWith('super_admin');
  };
  // Fallback org name if orgName is not set, but user has a staff/admin role
  const fallbackOrgName = roles.find(isStaffOrAdmin)?.split('-').slice(0, -1).join('-');

  // Get roles for the current org (or fallback org)
  const currentOrgRoles = roles.filter(role => {
    const roleOrg = role.split('-').slice(0, -1).join('-');
    return roleOrg === (orgName || fallbackOrgName);
  });

  const currentRole = currentOrgRoles.map(role => role?.split(':')[0])[0]?.split('-').at(-1) || 'learner';
  const currentPermissions = currentOrgRoles.map(role => role.split(':')[1]);

  const hasCreateCourse = currentRole === 'super_admin' || currentPermissions.includes('CreateCourse');
  const hasCreateProgram = currentRole === 'super_admin' || currentPermissions.includes('CreateProgram');
  const hasViewAnalytics = currentRole === 'super_admin' || currentPermissions.includes('ViewAnalytics');
  const hasLibraryView = currentRole === 'super_admin' || currentPermissions.includes('LibraryView');
  const hasLibraryCreate = currentRole === 'super_admin' || currentPermissions.includes('LibraryCreate');
  const hasScheduleCreate = currentRole === 'super_admin' || currentPermissions.includes('ScheduleCreate');
  const hasCertificateView = currentRole === 'super_admin' || currentPermissions.includes('CertificateView');
  const hasNotificationSend = currentRole === 'super_admin' || currentPermissions.includes('NotificationSend');
  const hasBillingView = currentRole === 'super_admin' || currentPermissions.includes('BillingView');
  const hasUserManagement = currentRole === 'super_admin' || currentPermissions.includes('UserManagement');
  const hasPlatformSettings = currentRole === 'super_admin' || currentPermissions.includes('PlatformSettings');
  const hasAiStudio = currentRole === 'super_admin' || currentPermissions.includes('AiStudio');

  return {
    role: currentRole,
    permissions: currentPermissions,
    hasCreateCourse,
    hasCreateProgram,
    hasViewAnalytics,
    hasLibraryView,
    hasLibraryCreate,
    hasScheduleCreate,
    hasCertificateView,
    hasNotificationSend,
    hasBillingView,
    hasUserManagement,
    hasPlatformSettings,
    hasAiStudio
  };
}
